import { Layout } from "react-grid-layout";

import { isLayoutEqual } from "./is-layout-equal";

export type LayoutSaveQueueState = {
  lastPersisted: Layout[];
  inFlight: Layout[] | null;
  queued: Layout[] | null;
};

export function createLayoutSaveQueueState(initial: Layout[]): LayoutSaveQueueState {
  return {
    lastPersisted: initial,
    inFlight: null,
    queued: null,
  };
}

/**
 * Latest layout is ready to persist. Starts a mutation when idle, otherwise
 * stashes a single trailing queued payload (network latency is the debounce).
 */
export function enqueueLayoutSave(
  state: LayoutSaveQueueState,
  payload: Layout[],
): { state: LayoutSaveQueueState; shouldMutate: Layout[] | null } {
  if (state.inFlight) {
    if (isLayoutEqual(payload, state.inFlight)) {
      return { state: { ...state, queued: null }, shouldMutate: null };
    }
    return { state: { ...state, queued: payload }, shouldMutate: null };
  }

  if (isLayoutEqual(payload, state.lastPersisted)) {
    return { state: { ...state, queued: null }, shouldMutate: null };
  }

  return {
    state: { ...state, inFlight: payload, queued: null },
    shouldMutate: payload,
  };
}

/**
 * In-flight mutation settled. Flushes queued payload immediately when still dirty.
 */
export function onSaveSettle(
  state: LayoutSaveQueueState,
  outcome: "success" | "error",
): { state: LayoutSaveQueueState; shouldMutate: Layout[] | null } {
  const lastPersisted =
    outcome === "success" && state.inFlight ? state.inFlight : state.lastPersisted;

  const afterSettle: LayoutSaveQueueState = {
    lastPersisted,
    inFlight: null,
    queued: state.queued,
  };

  if (afterSettle.queued && !isLayoutEqual(afterSettle.queued, afterSettle.lastPersisted)) {
    const payload = afterSettle.queued;
    return {
      state: { ...afterSettle, inFlight: payload, queued: null },
      shouldMutate: payload,
    };
  }

  return {
    state: { ...afterSettle, queued: null },
    shouldMutate: null,
  };
}

/**
 * A container was added or removed on the server. Rebase the persisted baseline
 * onto the new set of ids: keep the baseline geometry of containers we already
 * knew (so pending local edits stay dirty) and accept the server geometry for
 * new ones (so their arrival alone does not look like an edit).
 */
export function onStructuralSync(
  state: LayoutSaveQueueState,
  serverLayout: Layout[],
): LayoutSaveQueueState {
  const baselineById = new Map(state.lastPersisted.map((item) => [item.i, item]));

  const rebased = serverLayout.map(
    (serverItem) => baselineById.get(serverItem.i) ?? serverItem,
  );

  if (isLayoutEqual(rebased, state.lastPersisted)) return state;

  return { ...state, lastPersisted: rebased };
}

/** True while a request is in flight or a trailing save is queued. */
export function isLayoutSavePending(state: LayoutSaveQueueState): boolean {
  return state.inFlight !== null || state.queued !== null;
}
